import { useEffect, useState } from "react"
import { X } from "lucide-react"

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer"
import type { AppLocale } from "@/shared/i18n/translations"
import { useI18n } from "@/shared/i18n"
import { useLocaleStore } from "@/shared/store/localeStore"
import { cn } from "@/shared/lib/utils"

import flagEnUrl from "./assets/flag-en.svg?url"
import flagRuUrl from "./assets/flag-ru.svg?url"

interface Props {
  open: boolean
  onClose: () => void
}

const OPTIONS: { locale: AppLocale; flag: "gb" | "ru" }[] = [
  { locale: "en", flag: "gb" },
  { locale: "ru", flag: "ru" },
  { locale: "tonru", flag: "ru" },
]

function LocaleFlag({ variant }: { variant: "gb" | "ru" }) {
  const src = variant === "gb" ? flagEnUrl : flagRuUrl
  return (
    <img
      src={src}
      alt=""
      className="size-8 shrink-0 rounded-full object-cover"
      aria-hidden
    />
  )
}

function LocaleRadio({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "relative flex size-4 shrink-0 items-center justify-center rounded-[8px]",
        selected ? "bg-[#00FE74]" : "border border-solid border-white/[0.24]"
      )}
      aria-hidden
    >
      {selected ? (
        <span className="size-2 shrink-0 rounded-full bg-[#1B1C21]" />
      ) : null}
    </span>
  )
}

export function LanguageSheet({ open, onClose }: Props) {
  const { t } = useI18n()
  const activeLocale = useLocaleStore((s) => s.locale)
  const setLocale = useLocaleStore((s) => s.setLocale)

  const [pending, setPending] = useState<AppLocale>(activeLocale)

  useEffect(() => {
    if (open) setPending(activeLocale)
  }, [open, activeLocale])

  const labelFor = (locale: AppLocale) => {
    if (locale === "en") return t("profile.localeEnglish")
    if (locale === "ru") return t("profile.localeRussian")
    return t("profile.localeTonru")
  }

  const handleConfirm = () => {
    setLocale(pending)
    onClose()
  }

  return (
    <Drawer open={open} onOpenChange={(v) => !v && onClose()}>
      <DrawerContent
        showDefaultHandle={false}
        overlayClassName="bg-black/60 backdrop-blur-[5px]"
        className={cn(
          "flex max-h-[90vh] flex-col rounded-t-[24px]",
          "border border-solid border-white/[0.04] bg-[#24242E] px-0"
        )}
      >
        <div className="flex h-4 w-full shrink-0 items-center justify-center pt-2">
          <div className="h-1 w-8 rounded-full bg-white/10" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-14 shrink-0 items-stretch">
            <div className="flex min-w-0 flex-1 flex-col justify-center px-4">
              <DrawerTitle className="text-left text-2xl font-semibold leading-[29px] tracking-[-0.5px] text-white">
                {t("profile.languageSheetTitle")}
              </DrawerTitle>
            </div>
            <DrawerClose asChild>
              <button
                type="button"
                className="flex size-14 shrink-0 items-center justify-center p-4 text-white transition-opacity hover:opacity-90 active:opacity-80"
              >
                <X className="size-6" strokeWidth={2} aria-hidden />
              </button>
            </DrawerClose>
          </div>

          <DrawerDescription className="sr-only">
            {t("profile.languageSheetDescription")}
          </DrawerDescription>

          <div
            role="radiogroup"
            aria-label={t("profile.languageSheetTitle")}
            className="flex shrink-0 flex-col"
          >
            {OPTIONS.map(({ locale, flag }) => (
              <button
                key={locale}
                type="button"
                role="radio"
                aria-checked={pending === locale}
                className="flex h-14 w-full items-center gap-2 px-4 py-3 text-left outline-none transition-colors focus-visible:bg-white/[0.06]"
                onClick={() => setPending(locale)}
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <LocaleFlag variant={flag} />
                  <span className="truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                    {labelFor(locale)}
                  </span>
                </span>
                <LocaleRadio selected={pending === locale} />
              </button>
            ))}
          </div>

          <div className="mt-auto shrink-0 px-4 pb-6 pt-4">
            <button
              type="button"
              className="flex h-12 w-full items-center justify-center rounded-2xl bg-[#7E7D9B] text-base font-bold leading-[19px] tracking-[-0.5px] text-white transition-opacity hover:opacity-95 active:opacity-90"
              onClick={handleConfirm}
            >
              {t("profile.languageConfirm")}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
